/**
 * The Brand filter on /products, /media and products bulk edit. The page URL keeps brand ids
 * (`vendorIds`, resolution 7); links made before T-196 held brand names under `vendors`, which
 * the pages still read (decision 30 c). Until a name is matched to its brand, it's sent as a name.
 */
export type BrandFilter = { ids: string[]; names: string[] };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isBrandId(value: string): boolean {
  return UUID_RE.test(value);
}

/** Ids and names apart: a brand id is a UUID, and no brand name is. */
export function splitBrandRefs(refs: readonly string[]): BrandFilter {
  const ids: string[] = [];
  const names: string[] = [];
  for (const raw of refs) {
    const ref = raw.trim();
    if (!ref) continue;
    const list = isBrandId(ref) ? ids : names;
    if (!list.includes(ref)) list.push(ref);
  }
  return { ids, names };
}

/**
 * Old links' names as ids, once the brands are known: each ref that names a brand becomes that
 * brand's id; ids and unknown names stay as they are. Returns `refs` itself when nothing changes.
 */
export function resolveBrandRefs(
  refs: readonly string[],
  brands: readonly { id: string; name: string }[]
): readonly string[] {
  if (refs.every(isBrandId) || brands.length === 0) return refs;
  const idByName = new Map(brands.map((b) => [b.name, b.id]));
  const next = [...new Set(refs.map((ref) => (isBrandId(ref) ? ref : (idByName.get(ref) ?? ref))))];
  return next.length === refs.length && next.every((ref, i) => ref === refs[i]) ? refs : next;
}
